import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { TrendUp, Plus, Trash, CalendarBlank, PencilSimple, Rows, ChartBar } from "@phosphor-icons/react";

const STATUS = {
  previzionat: { label: "Previzionat", cls: "bg-blue-500/15 text-blue-300" },
  confirmat: { label: "Confirmat", cls: "bg-violet-500/15 text-violet-300" },
  realizat: { label: "Realizat", cls: "bg-emerald-500/15 text-emerald-300" },
  anulat: { label: "Anulat", cls: "bg-white/5 text-muted-foreground" },
};
const HORIZONS = [3, 6, 9, 12, 15, 18];
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const monthsLeft = (iso) => { if (!iso) return null; const d = Math.ceil((new Date(iso) - new Date()) / (1000 * 60 * 60 * 24 * 30)); return d; };

function isoWeek(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
  return { year: date.getUTCFullYear(), week };
}

const EMPTY = { lead_id: "", lead_company: "", project_type: "", signed: false, horizon_months: 6, install_estimate_date: "", estimated_value: 0, probability: 50, status: "previzionat", notes: "" };

function ForecastDialog({ open, onClose, onSaved, editing, leads }) {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { setF(editing ? { ...EMPTY, ...editing } : EMPTY); }, [editing, open]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    try {
      if (editing) { const { data } = await api.put(`/forecasts/${editing.id}`, f); onSaved(data, true); }
      else { const { data } = await api.post("/forecasts", f); onSaved(data, false); }
      toast.success(editing ? "Previziune actualizată" : "Previziune adăugată"); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{editing ? "Editează previziunea" : "Previziune nouă"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <label className="text-xs font-bold uppercase text-muted-foreground">Lead asociat</label>
            <Select value={f.lead_id || "none"} onValueChange={(v) => set("lead_id", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="forecast-lead"><SelectValue placeholder="Alege lead…" /></SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="none">— fără lead (companie manuală) —</SelectItem>
                {leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.company}</SelectItem>)}
              </SelectContent>
            </Select>
            {!f.lead_id && <Input placeholder="Companie (manual)" value={f.lead_company} onChange={(e) => set("lead_company", e.target.value)} data-testid="forecast-company" />}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <label className="text-xs font-bold uppercase text-muted-foreground">Tip proiect</label>
              <Input value={f.project_type} onChange={(e) => set("project_type", e.target.value)} placeholder="ex. Rezervor 500mc" data-testid="forecast-type" />
            </div>
            <div className="grid gap-1.5">
              <label className="text-xs font-bold uppercase text-muted-foreground">Orizont (luni)</label>
              <Select value={String(f.horizon_months)} onValueChange={(v) => set("horizon_months", Number(v))}>
                <SelectTrigger data-testid="forecast-horizon"><SelectValue /></SelectTrigger>
                <SelectContent>{HORIZONS.map((h) => <SelectItem key={h} value={String(h)}>{h} luni</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <label className="text-xs font-bold uppercase text-muted-foreground">Dată estimată instalare</label>
              <Input type="date" value={(f.install_estimate_date || "").slice(0, 10)} onChange={(e) => set("install_estimate_date", e.target.value)} data-testid="forecast-date" />
            </div>
            <div className="grid gap-1.5">
              <label className="text-xs font-bold uppercase text-muted-foreground">Valoare estimată (lei)</label>
              <Input type="number" value={f.estimated_value} onChange={(e) => set("estimated_value", e.target.value)} data-testid="forecast-value" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <label className="text-xs font-bold uppercase text-muted-foreground">Probabilitate %</label>
              <Input type="number" min="0" max="100" value={f.probability} onChange={(e) => set("probability", e.target.value)} data-testid="forecast-prob" />
            </div>
            <div className="grid gap-1.5">
              <label className="text-xs font-bold uppercase text-muted-foreground">Status</label>
              <Select value={f.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="forecast-status"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.signed} onChange={(e) => set("signed", e.target.checked)} data-testid="forecast-signed" />
            Contract de construcții deja semnat
          </label>
          <div className="grid gap-1.5">
            <label className="text-xs font-bold uppercase text-muted-foreground">Note</label>
            <Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="forecast-notes" />
          </div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="forecast-save-btn">{editing ? "Salvează" : "Adaugă"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Forecasts() {
  const { hasRole } = useAuth();
  const canEdit = hasRole("vanzari", "asistent_vanzari", "pm", "admin");
  const [items, setItems] = useState([]);
  const [leads, setLeads] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [periodView, setPeriodView] = useState("month");

  const load = () => api.get("/forecasts").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
  }, []);

  const onSaved = () => { load(); };
  const del = async (id) => { try { await api.delete(`/forecasts/${id}`); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const openNew = () => { setEditing(null); setDlg(true); };
  const openEdit = (f) => { setEditing(f); setDlg(true); };

  const totalPipeline = items.filter((f) => f.status !== "anulat").reduce((a, f) => a + (f.estimated_value || 0) * (f.probability || 0) / 100, 0);

  const agg = useMemo(() => {
    const map = {};
    items.filter((f) => f.status !== "anulat" && f.install_estimate_date).forEach((f) => {
      const d = new Date(f.install_estimate_date);
      let key, label;
      if (periodView === "week") {
        const w = isoWeek(d);
        key = `${w.year}-W${String(w.week).padStart(2, "0")}`;
        label = `S${w.week}·${String(w.year).slice(2)}`;
      } else {
        key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        label = d.toLocaleDateString("ro-RO", { month: "short", year: "2-digit" });
      }
      if (!map[key]) map[key] = { key, label, count: 0, value: 0, weighted: 0 };
      map[key].count += 1;
      map[key].value += f.estimated_value || 0;
      map[key].weighted += (f.estimated_value || 0) * (f.probability || 0) / 100;
    });
    return Object.values(map).sort((a, b) => (a.key < b.key ? -1 : 1));
  }, [items, periodView]);

  return (
    <div className="mx-auto max-w-5xl px-6 py-8 fade-up" data-testid="forecasts-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">CRM · Planificare</p>
          <h1 className="font-head text-4xl font-extrabold tracking-tighter">Previziuni</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">Proiecte viitoare legate de lead-uri, cu orizont configurabil (instalarea rezervoarelor poate fi peste 6 / 10 / 18 luni).</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="rounded-xl border border-border bg-card px-4 py-2 text-right">
            <div className="text-xs text-muted-foreground">Pipeline ponderat</div>
            <div className="font-head text-xl font-extrabold text-emerald-400">{fmtMoney(totalPipeline)}</div>
          </div>
          {canEdit && <Button onClick={openNew} data-testid="forecast-new-btn"><Plus size={16} className="mr-1.5" /> Previziune nouă</Button>}
        </div>
      </div>

      <div className="mb-6 rounded-xl border border-border bg-card p-5" data-testid="forecast-timeline">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="flex items-center gap-2 font-head text-lg font-bold"><ChartBar size={18} weight="bold" className="text-emerald-400" /> Distribuție în timp ({agg.length} {periodView === "week" ? "săptămâni" : "luni"})</h3>
          <div className="flex overflow-hidden rounded-lg border border-border">
            <button onClick={() => setPeriodView("month")} data-testid="fc-view-month" className={`px-3 py-1.5 text-xs font-semibold ${periodView === "month" ? "bg-emerald-500/20 text-emerald-300" : "text-muted-foreground hover:bg-white/5"}`}>Lunar</button>
            <button onClick={() => setPeriodView("week")} data-testid="fc-view-week" className={`px-3 py-1.5 text-xs font-semibold ${periodView === "week" ? "bg-emerald-500/20 text-emerald-300" : "text-muted-foreground hover:bg-white/5"}`}>Săptămânal</button>
          </div>
        </div>
        {agg.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={agg}>
                <XAxis dataKey="label" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} interval={0} angle={agg.length > 10 ? -35 : 0} textAnchor={agg.length > 10 ? "end" : "middle"} height={agg.length > 10 ? 50 : 30} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={{ background: "#111827", border: "1px solid #1f2937", borderRadius: 8, fontSize: 12 }}
                  formatter={(v, n) => n === "count" ? [v, "Previziuni"] : [fmtMoney(v), "Valoare"]} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]} fill="#10B981" />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-3 overflow-hidden rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.02] text-xs uppercase text-muted-foreground"><tr>
                  <th className="px-3 py-2 text-left">Perioadă</th><th className="px-3 py-2 text-right">Previziuni</th>
                  <th className="px-3 py-2 text-right">Valoare estimată</th><th className="px-3 py-2 text-right">Pipeline ponderat</th>
                </tr></thead>
                <tbody className="divide-y divide-border">
                  {agg.map((r) => (
                    <tr key={r.key} data-testid={`fc-agg-${r.key}`}>
                      <td className="px-3 py-2 font-medium">{r.label}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.count}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-400">{fmtMoney(r.value)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{fmtMoney(r.weighted)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : <p className="text-sm text-muted-foreground">Nicio previziune cu dată de instalare pentru a construi graficul.</p>}
      </div>

      <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground"><Rows size={14} /> Listă previziuni</div>
      <div className="space-y-2">
        {items.map((f) => {
          const st = STATUS[f.status] || STATUS.previzionat;
          const ml = monthsLeft(f.install_estimate_date);
          return (
            <div key={f.id} className="flex flex-wrap items-center gap-4 rounded-xl border border-border bg-card p-4" data-testid={`forecast-${f.id}`}>
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-400"><TrendUp size={22} weight="bold" /></div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-bold">{f.lead_company || "—"}</span>
                  {f.signed && <span className="rounded bg-emerald-500/10 px-1.5 py-0.5 text-[11px] font-bold text-emerald-300">Contract semnat</span>}
                  <span className={`rounded px-2 py-0.5 text-[11px] font-bold ${st.cls}`}>{st.label}</span>
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">{f.project_type || "—"} · probabilitate {f.probability}%</div>
              </div>
              <div className="text-center">
                <div className="flex items-center gap-1 text-sm font-medium"><CalendarBlank size={14} /> {fmt(f.install_estimate_date)}</div>
                <div className="text-[11px] text-muted-foreground">{ml != null ? `în ~${ml} luni · orizont ${f.horizon_months}L` : `orizont ${f.horizon_months} luni`}</div>
              </div>
              <div className="text-right">
                <div className="font-head text-lg font-extrabold tabular-nums text-emerald-400">{fmtMoney(f.estimated_value)}</div>
                <div className="text-[11px] text-muted-foreground">valoare estimată</div>
              </div>
              {canEdit && (
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(f)} data-testid={`forecast-edit-${f.id}`}><PencilSimple size={16} /></Button>
                  <Button variant="ghost" size="icon" className="text-red-400" onClick={() => del(f.id)} data-testid={`forecast-del-${f.id}`}><Trash size={16} /></Button>
                </div>
              )}
            </div>
          );
        })}
        {items.length === 0 && <p className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">Nicio previziune. Adaugă un proiect viitor legat de un lead.</p>}
      </div>

      <ForecastDialog open={dlg} onClose={() => setDlg(false)} onSaved={onSaved} editing={editing} leads={leads} />
    </div>
  );
}
